import DashboardBuilderModel from "~/models/tracked/dashboardBuilderModel";
import Organization from "~/models/organization";
import IRepo from "~/interfaces/repo";
import Repo from "~/state/modules/repo";
import DashboardProvider from "~/providers/dashboardProvider";
import store from "~/state/store";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import {IPeekerOptions} from "~/services/peeker";
import {RelationType} from "~/models/relationType";
import Validatable from "~/mixins/validatable";
import Capable from "../../../common/mixins/capable";
import DashboardValidation from "~/models/navigableValidations/dashboardValidation";
import Selected from "../../../formability/dashboardBuilder/store/modules/selected";
import DashboardElement from "~/models/dashboards/dashboardElement";
import ElementHandler from "~/services/elementHandler";
import {DashboardElementType} from "~/enums/dashboards/dashboardElementType";
import Grid from "~/models/dashboards/elements/grid";
import Heading from "~/models/dashboards/elements/heading";
import Subheading from "~/models/dashboards/elements/subheading";
import Analytic from "~/models/dashboards/elements/analytic";
import MyActions from "~/models/dashboards/elements/myActions";
import {ObjectHelper} from "~/helpers/objectHelper";
import IRootBuilderModel from "~/interfaces/builderModelInterfaces";
import {INavigableValidatableModel} from "~/interfaces/navigableValidationInterfaces";
import {ICapable} from "~/interfaces/capableInterfaces";
import Vue from "vue";
import {IDashboardElement, IDashboardElementContainer} from "~/interfaces/dashboardInterfaces";
import {Hash} from "~/interfaces/hashInterfaces";
import {VersionType} from "~/enums/versionType";
import ListViewElement from "~/models/dashboards/elements/listViewElement";
import DashboardAssignment from "~/models/dashboards/dashboardAssignment";
import Shareable from "~/mixins/shareable";
import {DocumentType} from "~/enums/documentType";
import Share from "~/models/share";

export default class Dashboard extends Capable(Validatable(Shareable(DashboardBuilderModel))) implements IRootBuilderModel, INavigableValidatableModel<Dashboard>, ICapable {
  rootModel: Dashboard;
  displayName: string;
  _organization: Organization;
  _classKey = "dashboard";
  title: string;
  author: {first_name: string, last_name: string};
  is_published: boolean;
  attempted_to_publish: boolean;
  is_archived: boolean;
  appStore: string;
  dashboard_elements: Hash<IDashboardElement>;
  dashboard_assignments: Hash<DashboardAssignment>;
  seenLivePreviewTooltip: boolean;

  except = ['dashboard', 'createEmbeddedObject']

  static repo: IRepo<Dashboard, DashboardProvider> = new Repo<Dashboard, DashboardProvider>('dashboards', Dashboard, {}, new DashboardProvider(Dashboard, 'dashboard', 'dashboard'));
  repo: IRepo<this, DashboardProvider<this>>;

  addToRepo(): void {
    Dashboard.repo.add(this);
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      "dashboard_elements": {type: DashboardElement, relationType: RelationType.dictionary},
      "dashboard_assignments": {type: DashboardAssignment, relationType: RelationType.dictionary},
      'shares': { type: Share, relationType: RelationType.collection},
    };
  }

  get deepDashboardElementsArray(): IDashboardElement[] {
    const elements = [];
    for (const el of this.dashboardElementsArray) {
      elements.push(el);
      if (el instanceof DashboardElement) {
        elements.push(...el.dashboardElementsArray);
      }
    }
    return elements;
  }

  get dashboardElementsArray(): Array<IDashboardElement> {
    return ObjectHelper.toArray(this.dashboard_elements).sort((a, b) => a.sort_order - b.sort_order);
  }

  createEmbeddedObject = {
    dashboard_elements: (parent, json) => {
      return this.dashboardContentElementFactory(json['content_type']);
    },
    dashboard_assignments: (parent, json) => {
      return new DashboardAssignment(parent, json, "dashboard_assignments")
    }
  };

  initializeEmbedded(parent: IDashboardElementContainer & IDashboardElement, item: IDashboardElement, position: number, json: object = {}, selectItem: boolean = true) {
    if (parent.content_type !== DashboardElementType.Grid) {
      parent.elementHandler.updateElementPositions(parent, item, position);
    }
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    item.addToRepo();
    if (selectItem) {
      item.select();
    }

    Vue.set(parent[item.rootName], item.client_id, item);
    return item;
  }

  _elementHandler = new ElementHandler(this, Dashboard, 'dashboard_elements');
  get elementHandler(): ElementHandler {
    return this._elementHandler
  }

  appendDashboardAssignments(json) {
    const permittee = new DashboardAssignment(this, json, 'dashboard_assignments');
    this.trackCreationDeletion(permittee.client_id, null, permittee.asJson(), permittee);
    return permittee;
  }

  dashboardContentElementFactory(elementType): DashboardElement{
    let el: DashboardElement;
    switch (elementType) {
      case DashboardElementType.Grid:
        el = new Grid();
        break;
      case DashboardElementType.Heading:
        el = new Heading();
        break;
      case DashboardElementType.Subheading:
        el = new Subheading();
        break;
      case DashboardElementType.Analytic:
        el = new Analytic();
        break;
      case DashboardElementType.MyActions:
        el = new MyActions();
        break;
      case DashboardElementType.ListView:
        el = new ListViewElement();
        break;
      default:
        throw new Error('do not create a generic element you will regret it.');
    }
    return el;
  }

  get publishedStatus(): string {
    return this.is_published ? 'Published' : 'Unpublished';
  }

  static get provider(): DashboardProvider {
    return this.repo.provider;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  static get displayName(): string {
    return 'Dashboard';
  }

  get rootName() {
    return 'dashboard';
  }

  get storeName(): string {
    let root;
    if (this.appStore) {
      root = this.appStore;
    } else {
      root = this.rootName;
    }
    return `${root}Store`;
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Dashboard";
    }
  }

  get builderStoreName(): string {
    return this.storeName;
  }

  get versionType(): VersionType {
    return VersionType.dashboard;
  }

  get collectionKey() {
    return 'dashboards';
  }

  get classKey(): string {
    return "dashboard";
  }

  get docType(): DocumentType {
    return DocumentType.dashboard;
  }

  get selectedDashboardElement(): IDashboardElement {
    return Selected.element.get();
  }

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || {area: this.currentBuilderArea(), modelId: this.clientIdOrId});
    }
    this._hasChanged = newVal;
  }

  get hasSeenPreviewLiveDataHere(): boolean {
    this.seenLivePreviewTooltip = localStorage.getItem("formability_preview_live_data_help_balloon") !== null;
    return this.seenLivePreviewTooltip;
  }

  setHasSeenPreviewLiveDataHere() {
    this.seenLivePreviewTooltip = true;
    localStorage.setItem("formability_preview_live_data_help_balloon", "true");
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.builderStoreName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    store.commit(`${this.builderStoreName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //

  //***** validation ***** //
  _validation: DashboardValidation = new DashboardValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  //***** validation end ***** //
}